import { Box, useMediaQuery, useTheme } from "@mui/material";
import Lottie from "lottie-react";
import { useState, useEffect } from "react";
import RotoLogo from "../../assets/RotoLogo.json";
import { isMobileWebBrowser, mobile } from "src/utils/platformUtils";

interface SplashScreenProps {
  onVideoEnd: () => void;
  logoTargetRef: React.RefObject<HTMLElement>;
}

const SplashScreen = ({ onVideoEnd, logoTargetRef }: SplashScreenProps) => {
  const [isAnimationEnded, setIsAnimationEnded] = useState(false);
  const [targetPosition, setTargetPosition] = useState({ x: 0, y: 0, scale: 1 });
  const theme = useTheme();

  const isMobileWeb = isMobileWebBrowser();

  // For mobile (less than 600px)
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // For tablets (between 600px and 1200px)
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "lg"));

  // Calcular la posición final cuando la animación termina
  useEffect(() => {
    if (isAnimationEnded && logoTargetRef.current) {
      // Fail-safe: Ensure the window is scrolled to the top before measuring.
      // This guarantees the calculation is correct even if the user managed to scroll.
      window.scrollTo(0, 0);

      const targetRect = logoTargetRef.current.getBoundingClientRect();

      // Calculate the center position of the screen
      const centerX = window.innerWidth / 2;
      const centerY = window.innerHeight / 2;

      // Calculate how much we need to move from the center to the target
      const moveX = targetRect.left + targetRect.width / 2 - centerX;
      const moveY = targetRect.top + targetRect.height / 2 - centerY;

      //set the target position
      setTargetPosition({
        x: moveX,
        y: moveY,
        scale: isMobile ? 0.4 : isTablet ? 0.2 : 0.3, // Mobile: 0.4, Tablet: 0.2, Desktop: 0.3
      });

      // Dar tiempo para la animación de traslado antes de llamar a onVideoEnd
      setTimeout(() => {
        onVideoEnd();
      }, 1000);
    }
  }, [isAnimationEnded, logoTargetRef, isMobile, isTablet, onVideoEnd]);

  return (
    <Box
      sx={{
        position: isMobileWeb || !mobile ? "absolute" : "fixed",
        top: 0,
        left: 0,
        height: isMobileWeb || !mobile ? "100%" : "100vh",
        width: isMobileWeb || !mobile ? "100%" : "100vw",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "transparent",
        pointerEvents: "none", // Allow interaction with elements below
        zIndex: 9999,
      }}
    >
      <Lottie
        animationData={RotoLogo}
        loop={false}
        autoplay
        onComplete={() => {
          console.log("Animation complete");
          setIsAnimationEnded(true);
        }}
        style={{
          width: isMobile ? "100%" : isTablet ? "50%" : "70%",
          height: isMobile ? "100%" : isTablet ? "50%" : "70%",
          opacity: 1,
          transform: isAnimationEnded ? `translate(${targetPosition.x}px, ${targetPosition.y}px) scale(${targetPosition.scale})` : "scale(1) translate(0, 0)",
          transition: "transform 1s ease-in-out",
        }}
      />
    </Box>
  );
};

export default SplashScreen;
